import React, { useState, useEffect, useRef } from 'react';

import {
  CHANNELS,
  referralChannelAdvancedOptions,
  referralChannelPrimaryContentOptions
} from 'types';
import { useSelector } from 'react-redux';
import {
  selectChannelErrors,
  selectActiveChannel,
  selectDraftChannels
} from 'redux/channels';
import { InlineNotification, Link } from 'carbon-components-react';
import ChannelTypes from './channelTypes';
import { EmailChannelFormComponent } from './forms/email/emailChannel.component';
import { FallbackChannelFormComponent } from './forms/email/fallbackChannel.component';
import { WebHookChannelFormComponent } from './forms/webHook/webHookChannel.component';
import SWXAutomateComponent from './forms/SWXAutomate/SWXAutomate.component';

export const Sandbox: React.FC<{}> = () => {
  const [channelType, setChannelType] = useState<string>(CHANNELS.LEGACY);
  const [copiedActiveValue, setCopiedActiveValue] = useState<string>('');
  const [
    copiedAdvanceFeaturesValue,
    setCopiedAdvanceFeaturesValue
  ] = useState<string>('');
  const [
    copiedPrimaryContentOption,
    setCopiedPrimaryContentOption
  ] = useState<string>('');
  const channelErrors: any = useSelector(selectChannelErrors);
  const activeChannels: any = useSelector(selectActiveChannel);
  const draftChannel: any = useSelector(selectDraftChannels);
  const advancedOptions = useRef<referralChannelAdvancedOptions>();
  const primaryContentOptions = useRef<referralChannelPrimaryContentOptions>();

  const copyFromActive = () => {
    if (
      activeChannels?.id &&
      activeChannels.configurationType !== CHANNELS.FALLBACK
    ) {
      const {
        configurationType,
        LegacyEmailConfiguration,
        WebhookConfiguration
      } = activeChannels;

      if (
        activeChannels.maxEnquiryArea !== null ||
        activeChannels.referralSummaryEmail !== null
      ) {
        const advanceFeaturesValues = {
          maxEnquiryArea: activeChannels?.maxEnquiryArea,
          splitEnquiries: activeChannels?.maxEnquiryArea ? true : false,
          referralSummaryEmail: activeChannels?.referralSummaryEmail || '',
          referralSummaryFrequency:
            activeChannels?.referralSummaryFrequency || ''
        };

        setCopiedAdvanceFeaturesValue(JSON.stringify(advanceFeaturesValues));
      } else {
        const advanceFeaturesValues = {
          maxEnquiryArea: 100000,
          splitEnquiries: false,
          referralSummaryEmail: activeChannels?.referralSummaryEmail || '',
          referralSummaryFrequency:
            activeChannels?.referralSummaryFrequency || ''
        };
        setCopiedAdvanceFeaturesValue(JSON.stringify(advanceFeaturesValues));
      }

      if (activeChannels.primaryContent !== null)
        setCopiedPrimaryContentOption(activeChannels.primaryContent);

      switch (configurationType) {
        case CHANNELS.LEGACY: {
          setChannelType(configurationType);
          const emailValues = {
            configurationType: CHANNELS.LEGACY,
            referralSummaryEmail:
              LegacyEmailConfiguration?.emailNormalEnquiries || '',
            isGifRequired: LegacyEmailConfiguration?.isGifRequired,
            isXmlRequired: LegacyEmailConfiguration?.isXmlRequired,
            isKmlRequired: LegacyEmailConfiguration?.isKmlRequired,
            isGmlRequired: LegacyEmailConfiguration?.isGmlRequired,
            isPdfRequired: LegacyEmailConfiguration?.isPdfRequired,
            gmlVersion: LegacyEmailConfiguration?.gmlVersion || '',
            gmlProjection: LegacyEmailConfiguration?.gmlProjection || ''
          };
          setCopiedActiveValue(JSON.stringify(emailValues));
          break;
        }
        case CHANNELS.FALLBACK: {
          setCopiedActiveValue('');
          break;
        }
        case CHANNELS.WEBHOOK: {
          setChannelType(configurationType);
          const customHeaders =
            WebhookConfiguration?.WebhookSubscription?.customHeaders;
          const webhookValues = {
            configurationType: CHANNELS.WEBHOOK,
            gmlProjection: WebhookConfiguration?.gmlProjection || '',
            url: WebhookConfiguration?.WebhookSubscription?.url || '',
            clientKey: WebhookConfiguration?.WebhookSubscription?.clientKey,
            customHeaders:
              customHeaders?.length > 0
                ? customHeaders
                : [{ name: '', value: '' }]
          };
          setTimeout(() => {
            setCopiedActiveValue(JSON.stringify(webhookValues));
          }, 0);
          break;
        }
        case CHANNELS.SWX_AUTOMATE: {
          setChannelType(configurationType);
          const automateValues = {
            configurationType: CHANNELS.SWX_AUTOMATE
          };
          setCopiedActiveValue(JSON.stringify(automateValues));
          break;
        }
      }
    }
  };

  const clearCopiedActiveValue = () => {
    setCopiedActiveValue('');
  };

  useEffect(() => {
    if (draftChannel?.configurationType && !draftChannel?.submitting) {
      setChannelType(draftChannel.configurationType);
      const savedAdvancedOptions = {
        splitEnquiries: typeof draftChannel?.maxEnquiryArea === 'number',
        maxEnquiryArea: draftChannel?.maxEnquiryArea
          ? draftChannel.maxEnquiryArea / 1e6
          : undefined,
        referralSummaryFrequency: draftChannel?.referralSummaryFrequency,
        referralSummaryEmail:
          draftChannel?.referralSummaryEmail?.replaceAll(';', ',') || ''
      };
      if (draftChannel?.primaryContent) {
        primaryContentOptions.current = {
          ...draftChannel?.primaryContent
        };
      } else {
        primaryContentOptions.current = draftChannel?.primaryContent;
      }

      if (advancedOptions?.current) {
        advancedOptions.current = {
          ...advancedOptions.current,
          ...savedAdvancedOptions
        };
      } else {
        advancedOptions.current = savedAdvancedOptions;
      }
    }
  }, [draftChannel]);

  return (
    <React.Fragment>
      <section className="form-section--item">
        {channelErrors.errors && (
          <InlineNotification
            lowContrast
            kind="error"
            iconDescription="describes the close button"
            subtitle={<span>{channelErrors.errors[0].message}</span>}
            title="Referral channel"
          />
        )}
        <h4 className="section__sub-heading">
          Configure and activate a referral channel
        </h4>
        <Link
          onClick={() => copyFromActive()}
          disabled={
            !activeChannels?.id ||
            activeChannels.configurationType === CHANNELS.FALLBACK
          }
        >
          Copy from the current active channel
        </Link>
      </section>
      <hr className="section__divider" />
      <ChannelTypes
        channelType={channelType}
        channelTypeHandler={(val: string) => setChannelType(val)}
      />
      <hr className="section__divider" />
      {channelType === CHANNELS.LEGACY && (
        <EmailChannelFormComponent
          advancedOptions={advancedOptions}
          copiedActiveValue={copiedActiveValue}
          copiedAdvanceFeaturesValue={copiedAdvanceFeaturesValue}
          copiedPrimaryContentOption={copiedPrimaryContentOption}
          clearCopiedActiveValue={clearCopiedActiveValue}
          primaryContentOptions={primaryContentOptions}
        />
      )}
      {channelType === CHANNELS.FALLBACK && (
        <FallbackChannelFormComponent
          advancedOptions={advancedOptions}
          copiedActiveValue={copiedActiveValue}
          copiedAdvanceFeaturesValue={copiedAdvanceFeaturesValue}
          clearCopiedActiveValue={clearCopiedActiveValue}
          primaryContentOptions={primaryContentOptions}
        />
      )}
      {channelType === CHANNELS.WEBHOOK && (
        <WebHookChannelFormComponent
          advancedOptions={advancedOptions}
          copiedActiveValue={copiedActiveValue}
          copiedAdvanceFeaturesValue={copiedAdvanceFeaturesValue}
          copiedPrimaryContentOption={copiedPrimaryContentOption}
          clearCopiedActiveValue={clearCopiedActiveValue}
          primaryContentOptions={primaryContentOptions}
        />
      )}
      {channelType === CHANNELS.SWX_AUTOMATE && (
        <SWXAutomateComponent
          primaryContentOptions={primaryContentOptions}
          advancedOptions={advancedOptions}
          copiedAdvanceFeaturesValue={copiedAdvanceFeaturesValue}
          copiedPrimaryContentOption={copiedPrimaryContentOption}
        />
      )}
    </React.Fragment>
  );
};

Sandbox.displayName = 'Sandbox';

export default React.memo(Sandbox);
